<template>
  <div class="row">
    <div class="col-4 custom-filepond">
      <file-pond
        name="filepond"
        ref="pond"
        label-idle="Drag & Drop your files or <span class='filepond--label-action'>Browse</span>"
        allow-multiple="true"
        accepted-file-types="image/jpeg, image/png, application/pdf"
        allow-reorder="true"
        :files="files"
        :credits="false"
        @updatefiles="handleFilePondUpdate"
        @reorderfiles="handleFilePondReorder"
      />
    </div>
    <div class="col-8">
      <div v-if="selectedImage" class="custom-image-preview">
        <img :src="selectedImage" alt="Preview" class="large-preview" />
      </div>
    </div>
  </div>
  <div v-if="isAny" class="row">
    <div class="col">
      <button class="btn btn-primary" :disabled="isLoading" @click="generatePdfAndUpload">
        <FontAwesomeIcon v-if="isLoading" icon="fa-solid fa-spinner" spin />
        <span class="ms-2">Upload Files</span>
      </button>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import vueFilePond from 'vue-filepond'
import { jsPDF } from 'jspdf'
import axios from 'axios'
import 'filepond/dist/filepond.min.css' // Base FilePond styles
import FilePondPluginImagePreview from 'filepond-plugin-image-preview' // Image Preview plugin
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css' // Image Preview
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type' // File Type Validation plugin
import * as pdfjsLib from 'pdfjs-dist'
// Dynamically import the correct worker for Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString()
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
// Create FilePond component with plugins
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview)
const apiUrl = window.config ? window.config.apiUrl : ''
export default {
  name: 'FilePreview',
  components: {
    FilePond,
    FontAwesomeIcon
  },
  props: {
    firm: {
      type: Number,
      required: true
    },
    ronumber: {
      type: String,
      required: true
    },
    token: {
      type: String,
      required: true
    },
    uploaded: {
      type: Function
    }
  },
  setup(props) {
    const saveUrl = `${apiUrl}/data/uploadFiles`
    const files = ref([])
    const fileUrls = ref({})
    const selectedImage = ref(null)
    const folder = 'invoices'
    const isLoading = ref(false)
    // Computed property to check if there are any files
    const isAny = computed(() => files.value.length > 0)
    // Handle file updates
    const handleFilePondUpdate = async (fileItems) => {
      files.value = []
      for (const fileItem of fileItems) {
        const file = fileItem.file
        if (file.type === 'application/pdf') {
          await convertPdfToImages(file)
        } else {
          files.value.push(file)
        }
      }
      attachClickEventToImages()
    }
    // Handle file reordering
    const handleFilePondReorder = (fileItems) => {
      files.value = fileItems.map((fileItem) => fileItem.file)
      attachClickEventToImages()
    }
    // Convert PDF to images and add them to FilePond
    const convertPdfToImages = async (file) => {
      const reader = new FileReader()
      reader.onload = async (event) => {
        const pdfData = new Uint8Array(event.target.result)
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise
        // store new files before updating the array
        let newFiles = []
        // loop through and convert each page to an image
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum)
          const scale = 1.5
          const viewport = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          canvas.width = viewport.width
          canvas.height = viewport.height
          await page.render({ canvasContext: context, viewport }).promise
          // convert canvas to Blob using async/await
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
          if (blob) {
            const fileName = `${file.name.replace('.pdf', '')}_page${pageNum}.png`
            const filePondFile = new File([blob], fileName, { type: 'image/png' })
            // store new files
            newFiles.push(filePondFile)
          }
        }
        // Ensure Vue updates the `files` array
        files.value = [...newFiles]
      }
      reader.readAsArrayBuffer(file)
    }
    // Attach click event to FilePond images for preview
    const attachClickEventToImages = () => {
      setTimeout(() => {
        // first, remove any previously attached event listeners
        document.querySelectorAll('.filepond--item').forEach((item) => {
          // remove any existing click event listener
          item.removeEventListener('click', handleClick)
        })
        // now, attach the new click event listener
        document.querySelectorAll('.filepond--item').forEach((item) => {
          const legend = item.querySelector('.filepond--file-wrapper legend').textContent
          if (legend) {
            // use an anonymous function to ensure the event is passed correctly
            item.addEventListener('click', (event) => handleClick(event, item))
          }
        })
      }, 500)
    }
    // Event handler function for click
    const handleClick = (event) => {
      const legend = event.currentTarget.querySelector('.filepond--file-wrapper legend')
      if (legend) {
        const fileName = legend.innerText
        const file = files.value.find((fileItem) => fileItem.name === fileName)
        if (file) {
          // check if URL already exists, otherwise create it
          if (!fileUrls.value[file.name]) {
            fileUrls.value[file.name] = URL.createObjectURL(file)
          }
          selectedImage.value = fileUrls.value[file.name]
        }
      }
    }
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous' // Prevent CORS issues
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          resolve(canvas.toDataURL('image/jpeg', 1.0)) // Ensures it's a standard JPEG
        }
        img.onerror = (error) => reject(error)
        img.src = URL.createObjectURL(file)
      })
    }
    // Generate PDF from images and upload
    const generatePdfAndUpload = async () => {
      selectedImage.value = null
      isLoading.value = true
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.width // Get PDF page width
      const pageHeight = doc.internal.pageSize.height // Get PDF page height
      // loop through the image files
      console.log('Generating PDF from images:', files.value)
      for (var i = 0; i < files.value.length; i++) {
        const file = files.value[i]
        if (file.type.startsWith('image')) {
          const imgBase64 = await fileToBase64(file) // Convert to base64
          // add image to PDF
          doc.addImage(imgBase64, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'SLOW', 0.5)
          // doc.addImage(imgBase64, 'PNG', 0, 0, pageWidth, pageHeight)
          // add a new page after the first image
          if (i < files.value.length - 1) {
            doc.addPage()
          }

          // selectedImage.value = imgBase64
        }
      }
      // for (const file of files.value) {
      //   if (file.type.startsWith('image')) {
      //     const imgUrl = URL.createObjectURL(file)
      //     // add image to PDF
      //     doc.addImage(imgUrl, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'SLOW', 0.5)
      //     // add a new page after the first image
      //     doc.addPage()
      //   }
      // }
      // save the generated PDF to a Blob
      const pdfBlob = doc.output('blob')
      // call upload function with the generated PDF
      await uploadFiles(pdfBlob)
    }
    // Upload files to backend
    const uploadFiles = async (pdfBlob) => {
      const formData = new FormData()
      let now = new Date().toISOString().replace(/[-:.]/g, '')
      let filename = `invoice_${props.ronumber}_${now}.pdf`
      formData.append('files', pdfBlob, filename)
      formData.append('firm', props.firm)
      formData.append('ronumber', props.ronumber)
      formData.append('folder', folder)
      formData.append('filename', filename)
      console.log('Uploading files:', {
        url: saveUrl,
        token: props.token,
        firm: props.firm,
        ronumber: props.ronumber
      })
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      try {
        const response = await axios({
          method: 'post',
          url: saveUrl,
          data: formData,
          cancelToken: source.token,
          headers: {
            'Content-Type': 'multipart/form-data', // Do not manually set Content-Type for FormData, axios handles it
            Authorization: `Bearer ${props.token}`
          }
        })
        isLoading.value = false
        if (props.uploaded) {
          props.uploaded(response.data) // Call the parent's method directly
        }
      } catch (error) {
        isLoading.value = false
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message)
        } else {
          console.error('Upload failed:', error)
        }
      }
    }

    onMounted(() => {
      attachClickEventToImages()
    })

    onBeforeUnmount(() => {
      // remove events
      document.querySelectorAll('.filepond--item').forEach((item) => {
        // Remove any existing click event listener
        item.removeEventListener('click', handleClick)
      })
    })

    return {
      files,
      isAny,
      selectedImage,
      handleFilePondUpdate,
      handleFilePondReorder,
      generatePdfAndUpload,
      isLoading
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep(.filepond--root) {
  height: 440px; /* Adjust this value as needed */
}

.custom-filepond {
  height: auto;
}
.custom-image-preview {
  padding: 5px;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  overflow: auto;
  max-height: 440px;
}
</style>
